// Client/src/services/NativeClientService.ts
import { SocketClient, SocketAuthErrorEvent } from './SocketClient';

type UnknownRecord = Record<string, unknown>;
type SocketHandler = (data: unknown) => void;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null;

const optionalString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const nullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

// Native-Core Socket Events
const NativeCoreSocketEvents = {
  ToCore: {
    ADMIN_COMMAND_FROM_SERVER: 'admin_command_from_server',
    ADMIN_COMMAND_STATUS: 'admin_command_status',
    NATIVE_STATUS_UPDATE: 'native_client_status_update',
    NATIVE_HEARTBEAT: 'native_client_heartbeat',
    REQUEST_RENDER_HEALTH: 'request_render_health',
    AUTH_ERROR: 'auth_error'
  },
  FromCore: {
    ADMIN_COMMAND_RESULT: 'admin_command_result',
    NATIVE_HEARTBEAT_PONG: 'native_client_pong',
    NATIVE_LOG_EVENT: 'native_log_event',
    NATIVE_RENDER_HEALTH: 'native_render_health'
  }
} as const;

interface NativeCoreRenderHealth {
  status: 'failed' | 'recovered';
  occurredAt: string;
  failingUrl?: string;
  errorCode?: number;
  errorDescription?: string;
  reason?: string;
  reloadPlannedAt?: string;
  reloadAttempt?: number;
  cooldownActive?: boolean;
  meta?: Record<string, unknown>;
}

interface ClientConnection {
  clientId: string;
  storeId: string;
  username: string;
  connectedAt: Date | null;
  lastSeen: Date | null;
  lastHeartbeatAt?: Date | null;
  pendingHeartbeatId?: string | null;
  pendingHeartbeatSentAt?: Date | null;
  isConnected: boolean;
  lastDisconnectReason?: string | null;
  displayName?: string;
  publicKey?: string;
  clientVersion?: string;
  platform?: string;
  connectionType?: string;
  clientFingerprint?: string | null;
  branch?: string | null;
  clientApiVersion?: string | null;
  supportedCommands?: string[] | null;
  renderHealth?: NativeCoreRenderHealth | null;
}

interface ClientStats {
  totalClients: number;
  activeClients: number;
  totalConnectionsToday: number;
  connectionsLastHour: number;
  averageConnectionDuration: number;
}

interface NativeClientStatusUpdate {
  activeClients: ClientConnection[];
  allClients?: ClientConnection[];
  degradedClients?: Array<{ clientId: string; username: string; storeId: string; reason: string }>;
  stats?: ClientStats;
  timestamp: string;
  heartbeatInterval?: number;
  heartbeatTimeout?: number;
}

interface NativeCoreCapabilities {
  available: boolean;
  socketPath?: string;
  socketNamespace?: string;
  version?: string;
  features?: string[];
  requiresClientVersion?: string;
  message?: string;
}

interface NativeClientConnectedEvent {
  clientId: string;
  employeeId?: string;
  username?: string;
  storeId?: string;
  connectionType?: string;
  branch?: string | null;
  clientApiVersion?: string | null;
  supportedCommands?: string[] | null;
  connectedAt?: string;
  reason?: string;
}

interface NativeClientDisconnectedEvent {
  clientId: string;
  employeeId?: string;
  username?: string;
  storeId?: string;
  disconnectedAt?: string;
  reason?: string;
  duration?: number;
}

interface AdminCommandPayload {
  commandId: string;
  commandType: string;
  commandData?: unknown;
  signature: string;
  timestamp: string;
  serverPublicKey?: string;
}

interface NativeLocalCommandResult extends UnknownRecord {
  success?: boolean;
  error?: string;
  errorMessage?: string | null;
  responseData?: UnknownRecord | null;
}

interface RenderIssueEvent {
  clientId: string;
  employeeId: string;
  storeId: string;
  reason: string;
  occurredAt: string;
}

interface RenderRecoveredEvent {
  clientId: string;
  employeeId: string;
  storeId: string;
  occurredAt: string;
}

const normalizeStringArray = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }

  const normalized = value
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter(Boolean);

  return normalized.length ? Array.from(new Set(normalized)) : null;
};

const extractCommandResponseData = (value: unknown): unknown => {
  if (isRecord(value) && isRecord(value.responseData)) {
    return value.responseData;
  }

  return value ?? null;
};

const normalizeStatusUpdate = (
  update: unknown,
  transform: (client: unknown) => ClientConnection
): NativeClientStatusUpdate => {
  const payload = isRecord(update) ? update : {};
  const activeClients = Array.isArray(payload.activeClients) ? payload.activeClients.map(transform) : [];
  const allClients = Array.isArray(payload.allClients) ? payload.allClients.map(transform) : undefined;
  const degradedClients = Array.isArray(payload.degradedClients)
    ? payload.degradedClients.filter(isRecord).map((client) => ({
        clientId: optionalString(client.clientId) || '',
        username: optionalString(client.username) || '',
        storeId: optionalString(client.storeId) || '',
        reason: optionalString(client.reason) || '',
      }))
    : [];

  return {
    activeClients,
    allClients,
    degradedClients,
    stats: isRecord(payload.stats) ? payload.stats as unknown as ClientStats : undefined,
    timestamp: optionalString(payload.timestamp) || new Date().toISOString(),
    heartbeatInterval: typeof payload.heartbeatInterval === 'number' ? payload.heartbeatInterval : undefined,
    heartbeatTimeout: typeof payload.heartbeatTimeout === 'number' ? payload.heartbeatTimeout : undefined,
  };
};

const readJsonRecord = async (response: Response): Promise<UnknownRecord> => {
  const data: unknown = await response.json();
  return isRecord(data) ? data : {};
};

class NativeClientService {
  private socketClient: SocketClient;
  private statusUpdateHandlers: Map<(update: NativeClientStatusUpdate) => void, SocketHandler>;
  private heartbeatHandlers: Map<(update: NativeClientStatusUpdate) => void, SocketHandler>;
  private connectedHandlers: Map<(event: NativeClientConnectedEvent) => void, SocketHandler>;
  private disconnectedHandlers: Map<(event: NativeClientDisconnectedEvent) => void, SocketHandler>;
  private commandStatusHandlers: Map<(data: unknown) => void, SocketHandler>;
  private renderIssueHandlers: Map<(event: RenderIssueEvent) => void, SocketHandler>;
  private renderRecoveredHandlers: Map<(event: RenderRecoveredEvent) => void, SocketHandler>;
  private commandUpdateReceived: Map<string, boolean> = new Map();
  private commandFromServerHandler: SocketHandler | null = null;
  private capabilities: NativeCoreCapabilities | null = null;
  private capabilitiesFetchedAt: number | null = null;

  constructor() {
    this.socketClient = new SocketClient();
    this.statusUpdateHandlers = new Map();
    this.heartbeatHandlers = new Map();
    this.connectedHandlers = new Map();
    this.disconnectedHandlers = new Map();
    this.commandStatusHandlers = new Map();
    this.renderIssueHandlers = new Map();
    this.renderRecoveredHandlers = new Map();
  }

  private normaliseClient(raw: unknown): ClientConnection {
    const client = isRecord(raw) ? raw : {};
    const toDate = (value: unknown): Date | null => {
      if (!value) return null;
      if (!(value instanceof Date) && typeof value !== 'string' && typeof value !== 'number') {
        return null;
      }
      const date = value instanceof Date ? value : new Date(value);
      return isNaN(date.getTime()) ? null : date;
    };

    return {
      clientId: optionalString(client.clientId) || optionalString(client.client_id) || '',
      storeId: optionalString(client.storeId) || optionalString(client.store_id) || 'unknown',
      username: optionalString(client.username) || optionalString(client.employeeId) || '',
      connectedAt: toDate(client.connectedAt),
      lastSeen: toDate(client.lastSeen),
      lastHeartbeatAt: toDate(client.lastHeartbeatAt),
      pendingHeartbeatId: nullableString(client.pendingHeartbeatId),
      pendingHeartbeatSentAt: toDate(client.pendingHeartbeatSentAt),
      isConnected: Boolean(client.isConnected),
      lastDisconnectReason: nullableString(client.lastDisconnectReason) ?? nullableString(client.reason),
      displayName: optionalString(client.displayName),
      publicKey: optionalString(client.publicKey),
      clientVersion: optionalString(client.clientVersion),
      platform: optionalString(client.platform),
      connectionType: optionalString(client.connectionType) || optionalString(client.connection_type),
      clientFingerprint: nullableString(client.clientFingerprint) ?? nullableString(client.client_fingerprint),
      branch: nullableString(client.branch),
      clientApiVersion: nullableString(client.clientApiVersion) ?? nullableString(client.client_api_version),
      supportedCommands: normalizeStringArray(client.supportedCommands ?? client.supported_commands),
      renderHealth: isRecord(client.renderHealth) ? client.renderHealth as unknown as NativeCoreRenderHealth : null
    };
  }

  private normalizeConnectedEvent(raw: unknown): NativeClientConnectedEvent {
    const event = isRecord(raw) ? raw : {};
    return {
      clientId: optionalString(event.clientId) || '',
      employeeId: optionalString(event.employeeId),
      username: optionalString(event.username),
      storeId: optionalString(event.storeId),
      connectionType: optionalString(event.connectionType),
      branch: nullableString(event.branch),
      clientApiVersion: nullableString(event.clientApiVersion),
      supportedCommands: normalizeStringArray(event.supportedCommands),
      connectedAt: optionalString(event.connectedAt),
      reason: optionalString(event.reason),
    };
  }

  private normalizeDisconnectedEvent(raw: unknown): NativeClientDisconnectedEvent {
    const event = isRecord(raw) ? raw : {};
    return {
      clientId: optionalString(event.clientId) || '',
      employeeId: optionalString(event.employeeId),
      username: optionalString(event.username),
      storeId: optionalString(event.storeId),
      disconnectedAt: optionalString(event.disconnectedAt),
      reason: optionalString(event.reason),
      duration: typeof event.duration === 'number' ? event.duration : undefined,
    };
  }

  private normalizeRenderIssueEvent(raw: unknown): RenderIssueEvent {
    const event = isRecord(raw) ? raw : {};
    return {
      clientId: optionalString(event.clientId) || '',
      employeeId: optionalString(event.employeeId) || '',
      storeId: optionalString(event.storeId) || '',
      reason: optionalString(event.reason) || 'Unbekanntes Render-Problem',
      occurredAt: optionalString(event.occurredAt) || new Date().toISOString(),
    };
  }

  private normalizeRenderRecoveredEvent(raw: unknown): RenderRecoveredEvent {
    const event = isRecord(raw) ? raw : {};
    return {
      clientId: optionalString(event.clientId) || '',
      employeeId: optionalString(event.employeeId) || '',
      storeId: optionalString(event.storeId) || '',
      occurredAt: optionalString(event.occurredAt) || new Date().toISOString(),
    };
  }

  async connect(
    employeeId?: string,
    _token?: string,
    options?: {
      connectionType?: 'admin' | 'native' | 'native-core';
      storeId?: string;
      clientFingerprint?: string;
      forceReconnect?: boolean;
    }
  ): Promise<boolean> {
    // Standardmäßig native-core verwenden; Legacy-Clients können den connectionType explizit setzen.
    const connectionType = options?.connectionType ?? 'native-core';

    const resolvedEmployeeId = employeeId ?? this.socketClient.currentEmployeeId ?? null;
    if (
      !options?.forceReconnect &&
      this.socketClient.isConnected &&
      this.socketClient.currentConnectionType === connectionType &&
      this.socketClient.currentEmployeeId === resolvedEmployeeId
    ) {
      return true;
    }

    return this.socketClient.connect({
      employeeId: resolvedEmployeeId ?? undefined,
      connectionType,
      storeId: options?.storeId,
      clientFingerprint: options?.clientFingerprint
    });
  }

  getCachedCapabilities(): NativeCoreCapabilities | null {
    return this.capabilities;
  }

  async fetchCapabilities(force = false): Promise<NativeCoreCapabilities> {
    const now = Date.now();
    const cacheIsFresh =
      !force &&
      this.capabilities &&
      this.capabilitiesFetchedAt &&
      now - this.capabilitiesFetchedAt < 60_000;

    if (cacheIsFresh) {
      return this.capabilities!;
    }

    try {
      const response = await fetch('/api/native-core/capabilities', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Capabilities request failed with status ${response.status}`);
      }

      const data = (await response.json()) as NativeCoreCapabilities;
      this.capabilities = data;
      this.capabilitiesFetchedAt = now;
      return data;
    } catch (error) {
      console.error('NativeClientService: Fehler beim Laden der Native-Core-Capabilities:', error);
      const fallback: NativeCoreCapabilities = this.capabilities ?? {
        available: false,
        message: 'Native Core Capabilities konnten nicht geladen werden.'
      };
      this.capabilities = fallback;
      this.capabilitiesFetchedAt = now;
      return fallback;
    }
  }

  onStatusUpdate(callback: (update: NativeClientStatusUpdate) => void): void {
    const handler = (update: unknown) => {
      const transform = (client: unknown) => this.normaliseClient(client);
      callback(normalizeStatusUpdate(update, transform));
    };

    this.statusUpdateHandlers.set(callback, handler);
    this.socketClient.on('native_client_status_update', handler);
  }

  offStatusUpdate(callback?: (update: NativeClientStatusUpdate) => void): void {
    if (!callback) {
      this.statusUpdateHandlers.forEach((handler) => {
        this.socketClient.off('native_client_status_update', handler);
      });
      this.statusUpdateHandlers.clear();
      return;
    }

    const handler = this.statusUpdateHandlers.get(callback);
    if (handler) {
      this.socketClient.off('native_client_status_update', handler);
      this.statusUpdateHandlers.delete(callback);
    }
  }

  onHeartbeat(callback: (update: NativeClientStatusUpdate) => void): void {
    const handler = (update: unknown) => {
      const transform = (client: unknown) => this.normaliseClient(client);
      callback(normalizeStatusUpdate(update, transform));
    };

    this.heartbeatHandlers.set(callback, handler);
    this.socketClient.on('native_client_heartbeat', handler);
  }

  offHeartbeat(callback?: (update: NativeClientStatusUpdate) => void): void {
    if (!callback) {
      this.heartbeatHandlers.forEach((handler) => {
        this.socketClient.off('native_client_heartbeat', handler);
      });
      this.heartbeatHandlers.clear();
      return;
    }

    const handler = this.heartbeatHandlers.get(callback);
    if (handler) {
      this.socketClient.off('native_client_heartbeat', handler);
      this.heartbeatHandlers.delete(callback);
    }
  }

  onClientConnected(callback: (event: NativeClientConnectedEvent) => void): void {
    const handler: SocketHandler = (event) => callback(this.normalizeConnectedEvent(event));
    this.socketClient.on('native_client_connected', handler);
    this.connectedHandlers.set(callback, handler);
  }

  onClientDisconnected(callback: (event: NativeClientDisconnectedEvent) => void): void {
    const handler: SocketHandler = (event) => callback(this.normalizeDisconnectedEvent(event));
    this.socketClient.on('native_client_disconnected', handler);
    this.disconnectedHandlers.set(callback, handler);
  }

  offClientConnected(callback?: (event: NativeClientConnectedEvent) => void): void {
    if (!callback) {
      this.connectedHandlers.forEach((handler) => {
        this.socketClient.off('native_client_connected', handler);
      });
      this.connectedHandlers.clear();
      return;
    }

    const handler = this.connectedHandlers.get(callback);
    if (handler) {
      this.socketClient.off('native_client_connected', handler);
      this.connectedHandlers.delete(callback);
    }
  }

  offClientDisconnected(callback?: (event: NativeClientDisconnectedEvent) => void): void {
    if (!callback) {
      this.disconnectedHandlers.forEach((handler) => {
        this.socketClient.off('native_client_disconnected', handler);
      });
      this.disconnectedHandlers.clear();
      return;
    }

    const handler = this.disconnectedHandlers.get(callback);
    if (handler) {
      this.socketClient.off('native_client_disconnected', handler);
      this.disconnectedHandlers.delete(callback);
    }
  }

  onCommandStatusUpdate(callback: (data: unknown) => void): void {
    const wrappedCallback = (data: unknown) => {
      const payload = isRecord(data) ? data : {};
      console.log('NativeClientService: admin_command_status empfangen:', data);
      const clientEmployeeId = optionalString(payload.clientEmployeeId);
      if (clientEmployeeId) {
        this.commandUpdateReceived.set(clientEmployeeId, true);
      }
      callback({
        ...payload,
        responseData: extractCommandResponseData(payload.responseData ?? payload.result),
        rawResult: payload.result ?? null
      });
    };
    this.commandStatusHandlers.set(callback, wrappedCallback);
    this.socketClient.on('admin_command_status', wrappedCallback);
  }

  offCommandStatusUpdate(callback?: (data: unknown) => void): void {
    if (!callback) {
      this.commandStatusHandlers.forEach((handler) => {
        this.socketClient.off('admin_command_status', handler);
      });
      this.commandStatusHandlers.clear();
      return;
    }

    const handler = this.commandStatusHandlers.get(callback);
    if (handler) {
      this.socketClient.off('admin_command_status', handler);
      this.commandStatusHandlers.delete(callback);
    }
  }

  /**
   * Registriert einen Listener für Render-Issue-Events
   */
  onRenderIssue(callback: (event: RenderIssueEvent) => void): void {
    const handler: SocketHandler = (event) => callback(this.normalizeRenderIssueEvent(event));
    this.socketClient.on('native_client_render_issue', handler);
    this.renderIssueHandlers.set(callback, handler);
  }

  /**
   * Entfernt einen Render-Issue-Listener
   */
  offRenderIssue(callback?: (event: RenderIssueEvent) => void): void {
    if (!callback) {
      this.renderIssueHandlers.forEach((handler) => {
        this.socketClient.off('native_client_render_issue', handler);
      });
      this.renderIssueHandlers.clear();
      return;
    }

    const handler = this.renderIssueHandlers.get(callback);
    if (handler) {
      this.socketClient.off('native_client_render_issue', handler);
      this.renderIssueHandlers.delete(callback);
    }
  }

  /**
   * Registriert einen Listener für Render-Recovered-Events
   */
  onRenderRecovered(callback: (event: RenderRecoveredEvent) => void): void {
    const handler: SocketHandler = (event) => callback(this.normalizeRenderRecoveredEvent(event));
    this.socketClient.on('native_client_render_recovered', handler);
    this.renderRecoveredHandlers.set(callback, handler);
  }

  /**
   * Entfernt einen Render-Recovered-Listener
   */
  offRenderRecovered(callback?: (event: RenderRecoveredEvent) => void): void {
    if (!callback) {
      this.renderRecoveredHandlers.forEach((handler) => {
        this.socketClient.off('native_client_render_recovered', handler);
      });
      this.renderRecoveredHandlers.clear();
      return;
    }

    const handler = this.renderRecoveredHandlers.get(callback);
    if (handler) {
      this.socketClient.off('native_client_render_recovered', handler);
      this.renderRecoveredHandlers.delete(callback);
    }
  }

  getCommandUpdateReceived(clientId: string): boolean {
    return this.commandUpdateReceived.get(clientId) || false;
  }

  clearCommandUpdateReceived(clientId: string): void {
    this.commandUpdateReceived.delete(clientId);
  }

  private normalizeAdminCommandPayload(raw: unknown): AdminCommandPayload | null {
    if (!isRecord(raw)) {
      return null;
    }

    const commandId = optionalString(raw.commandId);
    const commandType = optionalString(raw.commandType);
    const signature = optionalString(raw.signature);
    const timestamp = optionalString(raw.timestamp);

    if (!commandId || !commandType || !signature || !timestamp) {
      return null;
    }

    return {
      commandId,
      commandType,
      commandData: raw.commandData,
      signature,
      timestamp,
      serverPublicKey: optionalString(raw.serverPublicKey)
    };
  }

  // Native Client empfängt Commands vom Server und leitet sie an localhost:5000 weiter
  onCommandFromServer(callback: (command: AdminCommandPayload) => void): void {
    // Entferne vorherigen Handler, falls vorhanden
    if (this.commandFromServerHandler) {
      const isNativeCoreConnection = this.socketClient.currentConnectionType === 'native-core';
      const eventName = isNativeCoreConnection 
        ? NativeCoreSocketEvents.ToCore.ADMIN_COMMAND_FROM_SERVER
        : 'admin_command_from_server';
      
      this.socketClient.off(eventName, this.commandFromServerHandler);
      console.log('🔄 Native Client: Alter Command-Handler entfernt');
    }

    // Erstelle neuen Handler
    const handler: SocketHandler = async (rawCommandPayload) => {
      const commandPayload = this.normalizeAdminCommandPayload(rawCommandPayload);
      if (!commandPayload) {
        console.error('❌ Ungueltiger Native-Command-Payload:', rawCommandPayload);
        this.socketClient.send('admin_command_result', {
          success: false,
          result: null,
          error: 'Ungueltiger Native-Command-Payload',
          timestamp: new Date().toISOString()
        });
        return;
      }

      console.log('📥 Native Client empfängt Command:', commandPayload);

      try {
        let result: NativeLocalCommandResult;

        // Spezielle Handhabung für verschiedene Command-Types basierend auf server-types.ts
        switch (commandPayload.commandType) {
          case 'check_for_updates':
            result = await this.handleCheckForUpdates(commandPayload);
            break;
          case 'get_version':
            result = await this.handleGetVersion(commandPayload);
            break;
          case 'reboot':
          case 'apt_update':
          case 'apt_upgrade':
          case 'set_branch':
          case 'switch_branch':
          case 'validate_branch':
          case 'change_branch':
          case 'update_branch_url':
          case 'custom':
            result = await this.handleExecuteCommand(commandPayload);
            break;
          case 'token':
            result = await this.handleTokenRequest(commandPayload);
            break;
          case 'set_server_public':
            result = await this.handleSetServerPublic(commandPayload);
            break;
          default:
            throw new Error(`Unbekannter Command-Type: ${commandPayload.commandType}`);
        }

        console.log('✅ Command an localhost:5000 gesendet:', result);

        // Bestätige Command-Empfang an Server mit vollständiger Response
        const isNativeCoreConnection = this.socketClient.currentConnectionType === 'native-core';
        const resultEvent = isNativeCoreConnection 
          ? NativeCoreSocketEvents.FromCore.ADMIN_COMMAND_RESULT
          : 'admin_command_result';
        
        this.socketClient.send(resultEvent, {
          commandId: commandPayload.commandId,
          success: result.success || true,
          result: result, // Vollständige Response-Daten
          error: result.error,
          timestamp: new Date().toISOString()
        });

        // Callback aufrufen für UI-Updates
        if (callback) {
          callback(commandPayload);
        }

      } catch (error) {
        console.error('❌ Fehler beim Weiterleiten des Commands an localhost:5000:', error);

        // Fehler an Server zurückmelden mit vollständiger Info
        const isNativeCoreConnection = this.socketClient.currentConnectionType === 'native-core';
        const resultEvent = isNativeCoreConnection 
          ? NativeCoreSocketEvents.FromCore.ADMIN_COMMAND_RESULT
          : 'admin_command_result';
        
        this.socketClient.send(resultEvent, {
          commandId: commandPayload.commandId,
          success: false,
          result: null, // Keine Response-Daten bei Fehler
          error: error instanceof Error ? error.message : 'Unbekannter Fehler',
          timestamp: new Date().toISOString()
        });
      }
    };

    // Speichere Handler-Referenz
    this.commandFromServerHandler = handler;

    // Registriere Event-Handler basierend auf connectionType
    const isNativeCoreConnection = this.socketClient.currentConnectionType === 'native-core';
    
    if (isNativeCoreConnection) {
      // Native-Core: Verwende neue Event-Namen
      this.socketClient.on(NativeCoreSocketEvents.ToCore.ADMIN_COMMAND_FROM_SERVER, handler);
      console.log('✅ Native Client: Native-Core Command-Handler registriert');
    } else {
      // Legacy: Verwende alte Event-Namen
      this.socketClient.on('admin_command_from_server', handler);
      console.log('✅ Native Client: Legacy Command-Handler registriert');
    }
  }

  // Spezielle Handhabung für check_for_updates
  private async handleCheckForUpdates(commandPayload: AdminCommandPayload): Promise<NativeLocalCommandResult> {
    try {
      // Führe check_for_updates Command über /api/execute-command aus
      const response = await fetch('http://localhost:5000/api/execute-command', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          commandId: commandPayload.commandId,
          commandType: 'check_for_updates',
          commandData: commandPayload.commandData || {},
          signature: commandPayload.signature,
          timestamp: commandPayload.timestamp
        })
      });

      if (!response.ok) {
        throw new Error(`check_for_updates fehlgeschlagen: ${response.statusText}`);
      }

      const result = await readJsonRecord(response);

      // Hole zusätzlich die aktuelle Version vom /api/get-version Endpunkt
      const versionResponse = await fetch('http://localhost:5000/api/get-version', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      let currentVersion = 'unknown';
      if (versionResponse.ok) {
        const versionData = await readJsonRecord(versionResponse);
        currentVersion = optionalString(versionData.version) || 'unknown';
      }

      const responseData = isRecord(result.responseData) ? result.responseData : {};

      return {
        success: Boolean(result.success),
        commandId: commandPayload.commandId,
        responseData: {
          ...responseData,
          currentVersion,
          updateInstalled: Boolean(responseData.updateInstalled),
          lastCheck: new Date().toISOString()
        },
        errorMessage: optionalString(result.errorMessage) || null,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Fehler beim check_for_updates:', error);
      return {
        success: false,
        commandId: commandPayload.commandId,
        responseData: null,
        errorMessage: error instanceof Error ? error.message : 'Unbekannter Fehler',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Handhabung für get_version - nur Version abrufen, OHNE execute-command
  private async handleGetVersion(commandPayload: AdminCommandPayload): Promise<NativeLocalCommandResult> {
    try {
      const response = await fetch('http://localhost:5000/api/get-version', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Get-Version fehlgeschlagen: ${response.statusText}`);
      }

      const versionData = await readJsonRecord(response);

      return {
        success: true,
        commandId: commandPayload.commandId,
        responseData: {
          currentVersion: optionalString(versionData.version) || 'unknown',
          updateAvailable: false,
          lastCheck: new Date().toISOString()
        },
        errorMessage: null,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Fehler beim get_version:', error);
      return {
        success: false,
        commandId: commandPayload.commandId,
        responseData: null,
        errorMessage: error instanceof Error ? error.message : 'Unbekannter Fehler',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Handhabung für execute_command (alle Command-Types außer spezielle)
  private async handleExecuteCommand(commandPayload: AdminCommandPayload): Promise<NativeLocalCommandResult> {
    try {
      const response = await fetch('http://localhost:5000/api/execute-command', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          commandId: commandPayload.commandId,
          commandType: commandPayload.commandType,
          commandData: commandPayload.commandData,
          signature: commandPayload.signature,
          timestamp: commandPayload.timestamp
        })
      });

      if (!response.ok) {
        throw new Error(`Execute-Command fehlgeschlagen: ${response.statusText}`);
      }

      return await readJsonRecord(response);
    } catch (error) {
      console.error('Fehler beim Execute-Command:', error);
      return {
        success: false,
        commandId: commandPayload.commandId,
        responseData: null,
        errorMessage: error instanceof Error ? error.message : 'Unbekannter Fehler',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Handhabung für token
  private async handleTokenRequest(commandPayload: AdminCommandPayload): Promise<NativeLocalCommandResult> {
    try {
      const response = await fetch('http://localhost:5000/token', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Token-Request fehlgeschlagen: ${response.statusText}`);
      }

      return await readJsonRecord(response);
    } catch (error) {
      console.error('Fehler beim Token-Request:', error);
      return {
        success: false,
        commandId: commandPayload.commandId,
        responseData: null,
        errorMessage: error instanceof Error ? error.message : 'Unbekannter Fehler',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Handhabung für set_server_public
  private async handleSetServerPublic(commandPayload: AdminCommandPayload): Promise<NativeLocalCommandResult> {
    const commandData = isRecord(commandPayload.commandData) ? commandPayload.commandData : {};
    try {
      const response = await fetch('http://localhost:5000/set_server_public', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          server_public: optionalString(commandData.server_public) || commandPayload.serverPublicKey
        })
      });

      if (!response.ok) {
        throw new Error(`Set-Server-Public fehlgeschlagen: ${response.statusText}`);
      }

      return await readJsonRecord(response);
    } catch (error) {
      console.error('Fehler beim Set-Server-Public:', error);
      return {
        success: false,
        commandId: commandPayload.commandId,
        responseData: null,
        errorMessage: error instanceof Error ? error.message : 'Unbekannter Fehler',
        timestamp: new Date().toISOString()
      };
    }
  }

  offCommandFromServer(): void {
    if (this.commandFromServerHandler) {
      const isNativeCoreConnection = this.socketClient.currentConnectionType === 'native-core';
      const eventName = isNativeCoreConnection 
        ? NativeCoreSocketEvents.ToCore.ADMIN_COMMAND_FROM_SERVER
        : 'admin_command_from_server';
      
      this.socketClient.off(eventName, this.commandFromServerHandler);
      this.commandFromServerHandler = null;
      console.log('🧹 Native Client: Command-Handler entfernt');
    }
  }

  // Nachrichten senden (für Admin -> Native Client Kommunikation)
  sendCommand(clientId: string, commandType: string, commandData?: unknown): void {
    this.socketClient.send('admin_command', {
      clientId,
      commandType,
      commandData,
      timestamp: new Date().toISOString()
    });
  }

  sendCommandResult(commandId: string, success: boolean, result?: string, error?: string): void {
    this.socketClient.send('admin_command_result', {
      commandId,
      success,
      result,
      error,
      timestamp: new Date().toISOString()
    });
  }

  onAuthError(callback: (event: SocketAuthErrorEvent) => void): void {
    this.socketClient.on('auth_error', callback);
  }

  offAuthError(callback?: (event: SocketAuthErrorEvent) => void): void {
    this.socketClient.off('auth_error', callback);
  }

  // Status
  isConnected(): boolean {
    return this.socketClient.isConnected &&
           (
             this.socketClient.currentConnectionType === 'native' ||
             this.socketClient.currentConnectionType === 'native-core' ||
             this.socketClient.currentConnectionType === 'admin' ||
             this.socketClient.currentConnectionType === 'main'
           );
  }

  getConnectionInfo(): unknown {
    return {
      isConnected: this.socketClient.isConnected,
      employeeId: this.socketClient.currentEmployeeId,
      connectionType: this.socketClient.currentConnectionType,
      capabilities: this.capabilities
    };
  }

  disconnect(): void {
    // Entferne alle Handler beim Disconnect
    this.offStatusUpdate();
    this.offHeartbeat();
    this.offClientConnected();
    this.offClientDisconnected();
    this.offCommandStatusUpdate();
    this.offCommandFromServer();
    
    this.socketClient.disconnect();
  }
}

// Singleton-Instanz
const nativeClientService = new NativeClientService();
export default nativeClientService;
export { NativeClientService };
export type {
  ClientConnection,
  NativeClientStatusUpdate,
  NativeClientConnectedEvent,
  NativeClientDisconnectedEvent,
  NativeCoreCapabilities
};
